import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Form,
    Button,
    Alert,
    Container,
    Row,
    Col,
    Spinner,
    ListGroup,
    Collapse,
} from 'react-bootstrap';
import {
    FaTrashAlt,
    FaChevronDown,
    FaChevronUp,
    FaDownload, // Import Download Icon
} from 'react-icons/fa';
import api from '../services/api';

// --- Constants ---
const MAX_FILES = 25;
const API_ENDPOINTS = {
    LIST_FILES: 'drivers/list_files/',
    UPLOAD: 'drivers/upload/',
    DELETE_FILE: 'drivers/delete_file/',
    FILE_COLUMNS: (fileId) => `drivers/file_columns/${fileId}/`,
    // *** ADD Download Endpoint ***
    // Assumes endpoint takes file_name as query parameter
    DOWNLOAD_FILE: (fileName) => `drivers/download/?file_name=${encodeURIComponent(fileName)}`,
    // Or if it takes file_id (assuming file.id holds the correct identifier for download)
    // DOWNLOAD_FILE_BY_ID: (fileId) => `drivers/download/${fileId}/`,
};

/**
 * @component DriverDataUpload
 * @description Manages uploading/downloading SPSS (.sav) files for Driver Analysis.
 * Features include file upload/download, displaying a list of uploaded files,
 * loading data from a selected file, deleting files, and a collapsible
 * pane for the file list.
 */
function DriverDataUpload({ setColumns, setFileId, setBinaryColumns }) {
    // --- State Variables ---
    const [file, setFile] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [isFilePaneOpen, setIsFilePaneOpen] = useState(true);
    // *** ADD State for Downloading ***
    const [downloadingFile, setDownloadingFile] = useState(null); // Store name/id of file being downloaded

    // --- Refs ---
    const fileInputRef = useRef(null);

    // --- Effects ---
    useEffect(() => {
        fetchUploadedFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (file) {
            setErrorMessage('');
            setSuccessMessage('');
        }
    }, [file]);

    // --- Callbacks & Event Handlers ---

    const fetchUploadedFiles = useCallback(async () => {
        // ... (fetch logic remains the same) ...
        setErrorMessage('');
        setIsLoadingList(true);
        try {
            const response = await api.get(API_ENDPOINTS.LIST_FILES);
            const files = response.data.map((fileData) => ({
                name: fileData.name,
                // Assuming fileData.name is the correct identifier for load/delete/download
                // If backend uses a different ID (like file_id from upload response), store that too
                id: fileData.name, // Or fileData.file_id if available and needed
                columns: [],
                binaryColumns: [],
            }));
            setUploadedFiles(files);
        } catch (error) {
            console.error('Error fetching files:', error);
            setErrorMessage(
                'Failed to load previously uploaded files. Please try again.'
            );
            setUploadedFiles([]);
        } finally {
            setIsLoadingList(false);
        }
    }, []);


    const handleFileChange = (e) => {
        setFile(e.target.files?.[0] || null);
    };

    const handleFileUpload = async () => {
        // Clear previous messages
        setSuccessMessage('');
        setErrorMessage('');

        // --- Input Validations ---
        if (!file) {
            setErrorMessage('Please select a file to upload.');
            return;
        }
        if (uploadedFiles.length >= MAX_FILES) {
            setErrorMessage(
                `Maximum of ${MAX_FILES} files allowed. Please delete a file first.`
            );
            return;
        }

        // --- Prepare and Send Request ---
        const formData = new FormData();
        formData.append('file', file);
        setIsUploading(true);

        try {
            const response = await api.post(API_ENDPOINTS.UPLOAD, formData, {
                // Content-Type is usually set automatically by browser for FormData
                // headers: { 'Content-Type': 'multipart/form-data' }, // Usually not needed
            });

            // --- Handle Success ---
            // Destructure response data, providing default empty arrays
            const { file_id, columns = [], binary_columns = [] } = response.data;

            // Create an object holding the details of the newly uploaded file
            const newFileDetails = {
                name: file.name,        // Original file name from the user's machine
                id: file_id,            // Use the unique ID returned from the backend
                columns: columns,       // Columns list from the backend
                binaryColumns: binary_columns, // Binary columns list from the backend
            };

            // Update the list of uploaded files shown in the UI
            // Note: Using newFileDetails.name as the 'id' for consistency with fetchUploadedFiles logic.
            // Consider changing fetchUploadedFiles to use file_id if it's the true unique key.
            setUploadedFiles((prevFiles) => [...prevFiles, { ...newFileDetails, id: newFileDetails.name }]);

            // --- Update Parent Component State ---
            setColumns(newFileDetails.columns);             // Pass columns to parent
            setBinaryColumns(newFileDetails.binaryColumns); // Pass binary columns to parent (Corrected)
            setFileId(newFileDetails.name);                 // Pass file identifier (currently name) to parent

            // --- UI Cleanup ---
            setFile(null); // Clear the selected file in the input state
            setSuccessMessage(`File "${newFileDetails.name}" uploaded successfully.`); // Show success message
            setIsFilePaneOpen(true); // Ensure the file list pane is open to show the new file

            // Reset the actual file input element so the same file can be selected again if needed
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

        } catch (error) {
            // --- Handle Error ---
            console.error('Upload Error:', error.response || error.message || error);
            // Try to get specific error from backend response, otherwise show generic message
            const serverError =
                error.response?.data?.detail || // Check for DRF detail field
                error.response?.data?.error ||
                'Error uploading file. Please try again.';
            setErrorMessage(serverError); // Display error message to the user
        } finally {
            setIsUploading(false); // Reset uploading state regardless of success or failure
        }
    };

    const handleLoadFile = async (fileIdToLoad) => {
        setErrorMessage('');
        setSuccessMessage('');

        const selectedFile = uploadedFiles.find((f) => f.id === fileIdToLoad);

        if (!selectedFile) {
            setErrorMessage('Selected file not found in the list.');
            return;
        }

        // Work with a copy of the file details
        let fileDataToSet = { ...selectedFile };

        // Fetch columns only if they are missing or empty for this file
        if (!fileDataToSet.columns || fileDataToSet.columns.length === 0) {
            try {
                setSuccessMessage('Loading file details...'); // Provide loading feedback
                const response = await api.get(API_ENDPOINTS.FILE_COLUMNS(fileIdToLoad)); // Assuming endpoint uses name

                // Destructure response, provide defaults
                const { columns = [], binary_columns = [] } = response.data;

                // Update the file data object with fetched columns
                fileDataToSet = {
                    ...fileDataToSet,
                    columns: columns,
                    binaryColumns: binary_columns, // Correctly use destructured variable here
                };

                // Update the state of the specific file in the `uploadedFiles` array
                setUploadedFiles((prevFiles) =>
                    prevFiles.map((f) => (f.id === fileIdToLoad ? fileDataToSet : f))
                );
                setSuccessMessage(''); // Clear loading message

            } catch (error) {
                console.error('Error fetching file columns:', error);
                setErrorMessage(
                    'Failed to load file columns. Please try selecting it again.'
                );
                setSuccessMessage(''); // Clear loading message
                return; // Stop execution if fetching columns failed
            }
        }

        // --- Update Parent Component State ---
        // Use the properties from the (potentially updated) fileDataToSet object
        setColumns(fileDataToSet.columns);
        // *** ENSURE THIS LINE IS CORRECT IN YOUR SAVED FILE ***
        setBinaryColumns(fileDataToSet.binaryColumns);
        // *** ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ ***
        setFileId(fileDataToSet.id); // Use the ID from the fileDataToSet
        setSuccessMessage(`File "${fileDataToSet.name}" loaded successfully.`);
    };

    const handleFileDelete = async (fileNameToDelete) => {
        // ... (delete logic remains the same - uses fileNameToDelete) ...
        if (isUploading) return;
        setErrorMessage(''); setSuccessMessage('');
        if (!window.confirm(`Are you sure you want to delete "${fileNameToDelete}"?`)) { return; }
        try {
            const response = await api.delete(API_ENDPOINTS.DELETE_FILE, { data: { file_name: fileNameToDelete } });
            setSuccessMessage(response.data.message || 'File deleted successfully.');
            setUploadedFiles((prevFiles) => prevFiles.filter((f) => f.name !== fileNameToDelete));
        } catch (error) { /* ... error handling ... */
             console.error('Error deleting file:', error.response || error.message || error);
             const serverError = error.response?.data?.detail || error.response?.data?.error || 'Error deleting file.';
             setErrorMessage(serverError);
         }
    };

    /**
     * @function handleFileDownload
     * @description Initiates a download request for the specified file.
     * @param {string} fileName - The name of the file to download.
     */
    const handleFileDownload = async (fileName) => {
        if (downloadingFile) return; // Prevent multiple downloads at once

        setErrorMessage('');
        setSuccessMessage('');
        setDownloadingFile(fileName); // Set loading state for this specific file

        try {
            const response = await api.get(API_ENDPOINTS.DOWNLOAD_FILE(fileName), {
                responseType: 'blob', // Crucial: Tell axios to expect binary data
            });

            // Create a Blob URL
            const url = window.URL.createObjectURL(new Blob([response.data]));

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;

            // Try to get filename from Content-Disposition header, fallback to passed name
            let downloadFileName = fileName; // Default
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch && filenameMatch.length > 1) {
                    downloadFileName = filenameMatch[1];
                }
            }

            link.setAttribute('download', downloadFileName); // Set download attribute

            // Append to document and trigger click
            document.body.appendChild(link);
            link.click();

            // Clean up: remove link and revoke URL
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Download Error:', error.response || error.message || error);
            const serverError =
                error.response?.data?.detail ||
                error.response?.data?.error ||
                'Error downloading file. Please check server logs or network connection.';
            // Note: If the response was blob but contained error JSON, reading it is tricky
            // You might need additional logic to parse error blobs if the backend sends them
            setErrorMessage(serverError);
        } finally {
            setDownloadingFile(null); // Clear loading state
        }
    };


    const toggleFilePane = () => {
        setIsFilePaneOpen((prevState) => !prevState);
    };

    // --- Render Logic ---
    return (
        <Container fluid className="overflow-hidden">
            <Row className="mt-4">
                {/* === Upload Section === */}
                {/* Using md={6} as per previous request */}
                <Col md={6} className="p-3 mb-3 mb-md-0">
                    {/* ... (Upload H3, Form, Button, Feedback Area remain the same) ... */}
                    <h3 className="text-center mb-4"> Upload SPSS Data for Driver Analysis </h3>
                     <Form> <Form.Group controlId="fileUpload" className="mb-3"> <Form.Label>Select SPSS File (.sav):</Form.Label> <Form.Control type="file" onChange={handleFileChange} accept=".sav" ref={fileInputRef} disabled={isUploading} /> </Form.Group> <Button variant="primary" onClick={handleFileUpload} disabled={isUploading || !file} aria-label={ isUploading ? 'Uploading file' : 'Upload selected file' }> {isUploading ? ( <> <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"/> <span className="ms-1">Uploading...</span> </> ) : ( 'Upload File' )} </Button> </Form>
                     <div className="mt-3" style={{ minHeight: '58px' }}> {errorMessage && <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>{errorMessage}</Alert>} {successMessage && <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>{successMessage}</Alert>} </div>
                </Col>

                {/* === File List Section (Collapsible) === */}
                {/* Using md={4} - adjust if needed */}
                <Col md={4} className="bg-light p-3 rounded">
                    {/* Header Row with Toggle Button */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                       {/* ... (Header/Toggle remain the same) ... */}
                        <h5>Uploaded Files</h5> <Button variant="outline-secondary" size="sm" onClick={toggleFilePane} aria-controls="file-list-collapse" aria-expanded={isFilePaneOpen} title={isFilePaneOpen ? 'Hide File List' : 'Show File List'}> {isFilePaneOpen ? <FaChevronUp /> : <FaChevronDown />} <span className="visually-hidden"> {isFilePaneOpen ? 'Hide' : 'Show'} Files </span> </Button>
                    </div>

                    {/* Collapsible Area */}
                    <Collapse in={isFilePaneOpen}>
                        <div id="file-list-collapse">
                            {/* Content inside the collapsible area */}
                            {isLoadingList ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden"> Loading files... </span>
                                    </Spinner>
                                </div>
                            ) : uploadedFiles.length > 0 ? (
                                // No internal scroll, column will grow
                                <ListGroup>
                                    {uploadedFiles.map((uploadedFile) => (
                                        <ListGroup.Item
                                            key={uploadedFile.id}
                                            // Prevent action default for load button only
                                            // action // Removed action from item to avoid conflict
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            {/* File Name (Clickable to Load) */}
                                            <span
                                                style={{
                                                    wordBreak: 'break-all',
                                                    marginRight: '10px',
                                                    cursor: 'pointer', // Indicate clickable for load
                                                    flexGrow: 1, // Allow name to take up space
                                                }}
                                                title={`Load ${uploadedFile.name}`}
                                                onClick={() => handleLoadFile(uploadedFile.id)}
                                            >
                                                {uploadedFile.name}
                                            </span>

                                            {/* Action Buttons Wrapper */}
                                            <div className="d-flex flex-shrink-0 align-items-center">
                                                {/* Download Button */}
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-primary me-2" // Added margin-end
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent ListGroup item click
                                                        handleFileDownload(uploadedFile.name); // Pass name or ID
                                                    }}
                                                    aria-label={`Download file ${uploadedFile.name}`}
                                                    disabled={isUploading || downloadingFile === uploadedFile.name} // Disable while uploading or this file downloading
                                                    title={`Download ${uploadedFile.name}`}
                                                >
                                                    {downloadingFile === uploadedFile.name ? (
                                                         <Spinner animation="border" size="sm" />
                                                    ) : (
                                                         <FaDownload />
                                                    )}
                                                </Button>

                                                {/* Delete Button */}
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleFileDelete( uploadedFile.name );
                                                    }}
                                                    aria-label={`Delete file ${uploadedFile.name}`}
                                                    disabled={isUploading || !!downloadingFile} // Disable while uploading or *any* file downloading
                                                    title={`Delete ${uploadedFile.name}`}
                                                >
                                                    <FaTrashAlt />
                                                </Button>
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="text-muted text-center py-3">
                                    No files uploaded yet.
                                </p>
                            )}
                        </div>
                    </Collapse>
                </Col>
            </Row>
        </Container>
    );
}

// --- Prop Type Validation ---
DriverDataUpload.propTypes = {
    setColumns: PropTypes.func.isRequired,
    setFileId: PropTypes.func.isRequired,
    setBinaryColumns: PropTypes.func.isRequired,
};

export default DriverDataUpload;